import React, { CSSProperties } from "react";
import { useState, useRef, useEffect } from "react";
import "./style.css";

const fontUrl = "https://res.cloudinary.com/dre2qllr2/raw/upload/v1776255075/ABCMonumentGroteskMono-Regular_dlbqqs.otf";

const useFont = () => {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const font = new FontFace("MonumentGrotesk", `url(${fontUrl})`, { style: "normal", weight: "400", display: "swap" });
    font.load().then(f => { document.fonts.add(f); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);
  return loaded;
};

const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return mobile;
};

const BASE_FONT: CSSProperties = { fontFamily: "'MonumentGrotesk', 'Space Mono', monospace", textTransform: "uppercase", letterSpacing: "0.05em" };

// Tokens visuales — paleta colapsada a 4 tonos + acentos funcionales del wizard.
const T = {
  ink: "#000",
  mute: "#8a8a8a",
  rule: "#e5e5e5",
  paper: "#fff",
  success: "#1a7a40",
  successBg: "#f0faf4",
  successBorder: "#b7e4c7",
  warn: "#c57300",
  warnBg: "#fff8ed",
  warnBorder: "#ffd591",
  error: "#c0392b",
  errorBg: "#fdecea",
  errorBorder: "#f5b7b1",
  whatsapp: "#25D366",
};

const PRICES = {
  base: {
    jornada: { lv: 490, sdf: 735, hours: 9 },
    media: { lv: 290, sdf: 435, hours: 4 },
  } as Record<string, { lv: number; sdf: number; [k: string]: number }>,
  extraHour: { lv: 100, sdf: 150 },
  nightExtraHour: { lv: 150, sdf: 225 },
  // Limpieza estándar de fin de sesión, escalada por tramo de personas.
  cleaning: (bracket: string): number => {
    if (bracket === "medium") return 70;
    if (bracket === "large") return 90;
    if (bracket === "xlarge") return 110;
    if (bracket === "xxlarge") return 130;
    return 50; // small y por defecto
  },
  cycloramaPaint: 80,
  bracketSupplement: (bracket: string): { amount: number | null; label: string } => {
    if (bracket === "small") return { amount: 0, label: "1–10 personas" };
    if (bracket === "medium") return { amount: 150, label: "11–20 personas" };
    if (bracket === "large") return { amount: 350, label: "21–30 personas" };
    if (bracket === "xlarge") return { amount: 550, label: "31–40 personas" };
    if (bracket === "xxlarge") return { amount: 750, label: "41–50 personas" };
    if (bracket === "event") return { amount: null, label: "Más de 50 personas — a consultar" };
    return { amount: 0, label: "" };
  },
  vanSupplement: (vans: number): number => {
    if (vans <= 2) return 0;
    return 100 * (vans - 2);
  },
  mountDiscount: 0.5,
  // Fianza escalada por tramo de personas (riesgo de daños creciente).
  deposit: (bracket: string): number => {
    if (bracket === "large") return 800;
    if (bracket === "xlarge") return 1000;
    if (bracket === "xxlarge") return 1200;
    return 500; // small, medium y por defecto
  },
  // Aforo máximo autorizado según tramo (techo del tramo elegido).
  aforo: (bracket: string): number | null => {
    if (bracket === "small") return 10;
    if (bracket === "medium") return 20;
    if (bracket === "large") return 30;
    if (bracket === "xlarge") return 40;
    if (bracket === "xxlarge") return 50;
    return null; // event / consultar
  },
  iva: 0.21,
  irpf: 0.19, // Retención IRPF autónomos/empresa.
};

// Festivos nacionales + Comunidad de Madrid + Madrid capital (BOE/BOCM/Ayto.).
// Verifica anualmente con el calendario laboral oficial — los traslados varían.
const HOLIDAYS: Set<string> = new Set([
  // 2026
  "2026-01-01", "2026-01-06",
  "2026-03-19",
  "2026-04-02", "2026-04-03",
  "2026-05-01", "2026-05-02", "2026-05-15",
  "2026-07-25",
  "2026-08-15",
  "2026-10-12",
  "2026-11-02", "2026-11-09",
  "2026-12-07", "2026-12-08", "2026-12-25",
  // 2027
  "2027-01-01", "2027-01-06",
  "2027-03-19", "2027-03-25", "2027-03-26",
  "2027-05-03", "2027-05-15",
  "2027-08-16",
  "2027-10-12",
  "2027-11-01", "2027-11-09",
  "2027-12-06", "2027-12-08",
]);

const isFestivo = (yyyymmdd: string): boolean => HOLIDAYS.has(yyyymmdd);

const dayKindOf = (yyyymmdd: string): "lv" | "sdf" => {
  const d = new Date(yyyymmdd + "T00:00:00");
  const wd = d.getDay(); // 0 dom, 6 sáb
  if (wd === 0 || wd === 6) return "sdf";
  if (isFestivo(yyyymmdd)) return "sdf";
  return "lv";
};

const expandRange = (range: string): string[] => {
  if (!range) return [];
  const [s, e] = range.split("|");
  if (!s) return [];
  const start = new Date(s + "T00:00:00");
  const end = new Date((e || s) + "T00:00:00");
  const out: string[] = [];
  for (let d = new Date(start); d.getTime() <= end.getTime(); d.setDate(d.getDate() + 1)) {
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
};

const STEPS = ["Tipo", "Producción", "Fecha", "Datos", "Presupuesto"];

interface GalleryImage { src: string; alt: string; wide?: boolean; }

// Fotografía de Simone Marcolin — public_id fijados en Cloudinary (casayubero/simone/…).
// Orden narrativo intencional: 01 y 02 son apaisadas y abren a ancho completo,
// el resto son verticales y se emparejan de dos en dos, cerrando con la fachada (11).
const cldSimone = (id: string) => `https://res.cloudinary.com/dre2qllr2/image/upload/casayubero/simone/${id}`;

const SIMONE_GALLERY: GalleryImage[] = [
  { src: cldSimone("01"), alt: "Mesa comunal con estanterías y ciclorama al fondo", wide: true },
  { src: cldSimone("02"), alt: "Rincón de trabajo junto al patio ajardinado interior", wide: true },
  { src: cldSimone("03"), alt: "Ciclorama y escalera de acero bajo el pórtico estructural" },
  { src: cldSimone("04"), alt: "Patio ajardinado visto a través del ventanal de doble altura" },
  { src: cldSimone("05"), alt: "Columna de acero y ventanales correderos hacia el patio" },
  { src: cldSimone("06"), alt: "Espejo reflejando la instalación vista y la columna de hormigón" },
  { src: cldSimone("07"), alt: "Esquina acristalada del patio interior al atardecer" },
  { src: cldSimone("08"), alt: "Detalle de ducha exterior bajo la viga de hormigón" },
  { src: cldSimone("09"), alt: "Panel metálico plegable como divisoria del espacio" },
  { src: cldSimone("10"), alt: "Espejo reflejando la lámpara de papel y las sillas vintage" },
  { src: cldSimone("11"), alt: "Fachada de Casa Yubero en Calle Abolengo 12 al atardecer" },
];

// Puerta modular — mismo encuadre, cuatro grados de apertura navegables a clic.
const MODULAR_DOOR_IMAGES: GalleryImage[] = [
  { src: cldSimone("puerta-a"), alt: "Puerta modular — posición 1" },
  { src: cldSimone("puerta-b"), alt: "Puerta modular — posición 2" },
  { src: cldSimone("puerta-c"), alt: "Puerta modular — posición 3" },
  { src: cldSimone("puerta-d"), alt: "Puerta modular — posición 4" },
];

const galleryPages: GalleryImage[][] = [
  SIMONE_GALLERY,
  [
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1776253925/IMG_6887_swaior.jpg", alt: "Detalle luz natural sobre hormigón" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1776253925/01_04_2026_0034-4_copia_omxjlc.jpg", alt: "Nave diáfana brutalista" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1776253926/IMG_6993_m920ra.jpg", alt: "Patio ajardinado interior" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1776253929/IMG_6929_n6poi0.jpg", alt: "Cocina profesional inox" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1776253931/IMG_6928_qpsrg6.jpg", alt: "Vista general del espacio" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1780599320/cy_22_05_2026_0005.jpg", alt: "Ciclorama con luz natural directa" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1780599324/cy_22_05_2026_0016.jpg", alt: "Cercha de hormigón y ventanales del patio interior" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1780599326/cy_22_05_2026_0017.jpg", alt: "Vista general de la nave con mesa comunal" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1780599329/cy_22_05_2026_0018.jpg", alt: "Mesa comunal a contraluz" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1780599332/cy_22_05_2026_0024.jpg", alt: "Silla sobre ciclorama con luz de tarde" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1778417354/IMG_7414_ymdwpj.jpg", alt: "Nave con cercha blanca, ventanales y patio ajardinado" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1776253925/IMG_6927_lrq9vd.jpg", alt: "Equipo de rodaje trabajando en el estudio" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1776807869/05_02_2026_0019-4_jyycqc.jpg", alt: "Escritorio y silla frente al patio interior" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1776807871/05_02_2026_0020-4_h1a4vi.jpg", alt: "Ciclorama con obras apoyadas" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1776253931/IMG_6585_s4trjk.jpg", alt: "Arce japonés tras la malla del patio" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1776253925/645DEB6B-E107-4C86-B235-DD7B94BEF8BF_yjnnhz.jpg", alt: "Cercha de hormigón y arce del patio interior" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1780599322/cy_22_05_2026_0012.jpg", alt: "Fachada de Casa Yubero en Calle Abolengo 12" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1780599330/cy_22_05_2026_0021.jpg", alt: "Rodaje de moda frente al ciclorama" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1780600721/cy_22_05_2026_0007.jpg", alt: "Cocina profesional reflejada en el espejo" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1780600728/cy_22_05_2026_0010.jpg", alt: "Lavabo de acero inoxidable del aseo" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1780600719/cy_22_05_2026_0006.jpg", alt: "Instalación eléctrica vista en el techo" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1780600723/cy_22_05_2026_0008.jpg", alt: "Lámpara de papel y cortinas" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1780600725/cy_22_05_2026_0009.jpg", alt: "Bombilla reflejada en el espejo" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1780600730/cy_22_05_2026_0011.jpg", alt: "Detalle de azulejo en cuadrícula" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1780600732/cy_22_05_2026_0020.jpg", alt: "Cortinas y sillas a contraluz" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1776253924/IMG_6935_yxukzi.jpg", alt: "Luz de tarde sobre la pared" },
  ],
  [
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1776807877/IMG_5563_k9eyfb.jpg", alt: "Interior nave diáfana" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1776807876/IMG_5564_snlq5c.jpg", alt: "Rincón del estudio" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1776807873/IMG_5553_nlj7gq.jpg", alt: "Ciclorama 5×5" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1776807875/IMG_5558_n287sh.jpg", alt: "Luz natural sobre hormigón" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1776807869/01_04_2026_0028-5_hmc6uc.jpg", alt: "Vista amplia de la nave" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1776807869/01_04_2026_0032-5_ov6h42.jpg", alt: "Nave brutalista industrial" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1776807872/07_10_2025_0019_lwgudp.jpg", alt: "Set de rodaje en estudio" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1776808278/07_10_2025_0017_tpfyaz.jpg", alt: "Interior Casa Yubero" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1776807868/01_04_2026_0001-6_plgbcb.jpg", alt: "Detalle arquitectónico brutalista" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1776807872/05_02_2026_0014-4_w8iqze.jpg", alt: "El garaje original antes de la reforma" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1776807872/05_02_2026_0015-4_x8drfw.jpg", alt: "Detalle de celosías del taller original" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1776807869/05_02_2026_0016-4_ocneyg.jpg", alt: "Recorte de celosía en el muro original" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1776807875/IMG_5560_aprvmx.jpg", alt: "Detalle abstracto del proceso de obra" },
    { src: "https://res.cloudinary.com/dre2qllr2/image/upload/v1776807876/IMG_5579_iihdut.jpg", alt: "La nave durante la rehabilitación" },
  ],
];

// Inyecta transforms de Cloudinary (f_auto = WebP/AVIF, q_auto = compresión adaptativa).
const optimizeCld = (src: string, width?: number): string => {
  if (!src.includes("res.cloudinary.com") || !src.includes("/upload/")) return src;
  const parts = `f_auto,q_auto${width ? `,w_${width},c_limit` : ""}`;
  return src.replace("/upload/", `/upload/${parts}/`);
};

const ImgWithFallback = ({ src, alt, style, eager = false, width }: { src: string; alt: string; style: CSSProperties; eager?: boolean; width?: number }) => {
  const [err, setErr] = useState(false);
  return err ? (
    <div style={{ ...style, background: "#f0f0f0", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", padding: 20, boxSizing: "border-box" }}>
      <span style={{ fontSize: 13, color: "#8a8a8a", textAlign: "center" }}>{alt}</span>
      <a href={src} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: "#8a8a8a", marginTop: 8 }}>Abrir imagen ↗</a>
    </div>
  ) : (
    <img
      src={optimizeCld(src, width)}
      alt={alt}
      style={style}
      onError={() => setErr(true)}
      crossOrigin="anonymous"
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      {...({ fetchpriority: eager ? "high" : "auto" } as any)}
    />
  );
};

function Lightbox({ img, onClose }: { img: GalleryImage | null; onClose: () => void }) {
  if (!img) return null;
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.85)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", cursor: "zoom-out" }}>
      <img src={optimizeCld(img.src, 3000)} alt={img.alt} style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain" }} loading="eager" decoding="async" />
    </div>
  );
}

// Puerta modular — mismo encuadre, se cicla al clic entre los 4 grados de apertura (mismo
// mecanismo de crossfade por opacidad que HERO_IMAGES).
function ModularDoor({ images, isMobile }: { images: GalleryImage[]; isMobile: boolean }) {
  const [idx, setIdx] = useState(0);
  const advance = () => setIdx(i => (i + 1) % images.length);
  return (
    <div>
      <button
        onClick={advance}
        aria-label={`Puerta modular — posición ${idx + 1} de ${images.length}, clic para cambiar`}
        style={{
          display: "block", width: "100%", padding: 0, border: "none", background: "none", cursor: "pointer",
          position: "relative", aspectRatio: isMobile ? "4 / 5" : "16 / 9", overflow: "hidden",
        }}>
        {images.map((img, i) => (
          <img
            key={img.src}
            src={optimizeCld(img.src, 1600)}
            alt={img.alt}
            loading={i === 0 ? "eager" : "lazy"}
            decoding="async"
            style={{
              position: "absolute", inset: 0, width: "100%", height: "100%",
              objectFit: "cover",
              opacity: i === idx ? 1 : 0,
              transition: "opacity .5s ease",
            }}
          />
        ))}
      </button>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: isMobile ? "10px 16px 0" : "10px 48px 0" }}>
        <span style={{ fontSize: 12, color: "#8a8a8a", textTransform: "none", letterSpacing: "0.02em" }}>
          Pulsa para abrir la puerta
        </span>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: "#000", whiteSpace: "nowrap", marginLeft: 12 }}>
          {idx + 1} / {images.length}
        </span>
      </div>
    </div>
  );
}

function StepIndicator({ current }: { current: number }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px", marginBottom: 40, paddingBottom: 20, borderBottom: `1px solid ${T.rule}`, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em" }}>
      {STEPS.map((s, i) => (
        <span key={s} style={{ color: i === current ? T.ink : T.mute }}>
          {String(i + 1).padStart(2, "0")} {s}
        </span>
      ))}
    </div>
  );
}

interface SelectOption { value: string; label: string; detail?: string; icon?: string; }

function GridSelect({ label, value, onChange, options, columns = 2, sub }: {
  label: string; value: string; onChange: (v: string) => void;
  options: SelectOption[]; columns?: number; sub?: string;
}) {
  return (
    <div style={{ marginBottom: 32 }}>
      <label style={{ display: "block", fontSize: 11, fontWeight: 700, marginBottom: 12, color: "#8a8a8a", letterSpacing: "0.12em" }}>{label}</label>
      {sub && <p style={{ fontSize: 12, color: "#8a8a8a", margin: "0 0 12px" }}>{sub}</p>}
      <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${columns === 3 ? "120px" : "140px"}, 1fr))`, gap: 10 }}>
        {options.map(o => {
          const selected = value === o.value;
          return (
            <button key={o.value} onClick={() => onChange(o.value)} style={{
              padding: "15px 17px",
              border: selected ? "2px solid #000" : "1px solid #e5e5e5",
              background: "#fff",
              color: "#000",
              cursor: "pointer",
              textAlign: "left",
              transition: "border-color .15s",
            }}>
              <span style={{ display: "block", fontSize: 13, fontWeight: 700, letterSpacing: "0.05em" }}>{o.label}</span>
              {o.detail && <span style={{ display: "block", fontSize: 11, color: "#8a8a8a", marginTop: 4, letterSpacing: "0.03em", textTransform: "none" }}>{o.detail}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NumberInput({ label, value, onChange, min, max, sub }: {
  label: string; value: number; onChange: (v: number) => void;
  min?: number; max?: number; sub?: string;
}) {
  return (
    <div style={{ marginBottom: 28 }}>
      <label style={{ display: "block", fontSize: 11, fontWeight: 700, marginBottom: 8, color: "#8a8a8a", letterSpacing: "0.12em" }}>{label}</label>
      {sub && <p style={{ fontSize: 12, color: "#8a8a8a", margin: "0 0 10px", textTransform: "none", letterSpacing: "0.02em" }}>{sub}</p>}
      <input type="number" min={min} max={max} value={value} onChange={e => onChange(Number(e.target.value))}
        style={{ width: "100%", padding: "10px 2px", border: "none", borderBottom: "1px solid #e5e5e5", fontSize: 16, boxSizing: "border-box", fontFamily: "inherit", outline: "none", background: "transparent" }} />
    </div>
  );
}

function TextInput({ label, value, onChange, placeholder, type = "text", sub, name, autoComplete, inputMode, autoCapitalize, spellCheck, error }: {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; type?: string; sub?: string;
  name?: string;
  autoComplete?: string;
  inputMode?: "text" | "email" | "tel" | "url" | "numeric" | "decimal" | "search" | "none";
  autoCapitalize?: "off" | "none" | "on" | "sentences" | "words" | "characters";
  spellCheck?: boolean;
  error?: string;
}) {
  const inputId = name ? `field-${name}` : undefined;
  const borderColor = error ? "#c0392b" : "#e5e5e5";
  return (
    <div style={{ marginBottom: 20 }}>
      <label htmlFor={inputId} style={{ display: "block", fontSize: 11, fontWeight: 700, marginBottom: 8, color: "#8a8a8a", letterSpacing: "0.12em" }}>{label}</label>
      {sub && <p style={{ fontSize: 12, color: "#8a8a8a", margin: "0 0 10px", textTransform: "none", letterSpacing: "0.02em" }}>{sub}</p>}
      <input
        id={inputId}
        name={name}
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        autoCapitalize={autoCapitalize}
        spellCheck={spellCheck}
        style={{ width: "100%", padding: "10px 2px", border: "none", borderBottom: `1px solid ${borderColor}`, fontSize: 14, boxSizing: "border-box", fontFamily: "inherit", outline: "none", letterSpacing: "0.03em", background: "transparent" }}
      />
      {error && <p style={{ fontSize: 12, color: "#c0392b", margin: "6px 0 0", textTransform: "none", letterSpacing: "0.02em" }}>{error}</p>}
    </div>
  );
}

function Checkbox({ label, checked, onChange, detail }: {
  label: string; checked: boolean; onChange: () => void; detail?: string;
}) {
  return (
    <label style={{
      display: "flex", alignItems: "flex-start", gap: 14,
      padding: "15px 17px",
      border: checked ? "2px solid #000" : "1px solid #e5e5e5",
      cursor: "pointer", marginBottom: 10,
      background: "#fff",
      transition: "border-color .15s",
    }}>
      <input type="checkbox" checked={checked} onChange={onChange} style={{ marginTop: 2, accentColor: "#000", display: "none" }} />
      <div style={{
        width: 16, height: 16, flexShrink: 0, marginTop: 2,
        border: "2px solid #000",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {checked && <span style={{ fontSize: 11, color: "#000", fontWeight: 900 }}>✓</span>}
      </div>
      <div>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#000", display: "block" }}>{label}</span>
        {detail && <span style={{ display: "block", fontSize: 12, color: "#8a8a8a", marginTop: 3, textTransform: "none" }}>{detail}</span>}
      </div>
    </label>
  );
}

interface FormState {
  sessionType: string; dayType: string; modality: string; mountDays: number;
  peopleBracket: string; vans: number;
  name: string; company: string; nif: string; email: string; phone: string; projectDesc: string; date: string;
  privacyConsent: boolean; marketingConsent: boolean; irpfRetention: boolean;
}

const MONTHS_ES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const WEEKDAYS_ES = ["L", "M", "X", "J", "V", "S", "D"];

const ymd = (d: Date): string => {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const fmtRange = (start: string, end: string): string => {
  if (!start) return "";
  const s = new Date(start + "T00:00:00");
  if (!end || start === end) return `${s.getDate()} ${MONTHS_ES[s.getMonth()].toLowerCase()} ${s.getFullYear()}`;
  const e = new Date(end + "T00:00:00");
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  const sameYear = s.getFullYear() === e.getFullYear();
  if (sameMonth) return `${s.getDate()}–${e.getDate()} ${MONTHS_ES[s.getMonth()].toLowerCase()} ${s.getFullYear()}`;
  if (sameYear) return `${s.getDate()} ${MONTHS_ES[s.getMonth()].toLowerCase()} – ${e.getDate()} ${MONTHS_ES[e.getMonth()].toLowerCase()} ${s.getFullYear()}`;
  return `${s.getDate()} ${MONTHS_ES[s.getMonth()].toLowerCase()} ${s.getFullYear()} – ${e.getDate()} ${MONTHS_ES[e.getMonth()].toLowerCase()} ${e.getFullYear()}`;
};

function DateRangePicker({ label, value, onChange, sub }: {
  label: string; value: string; onChange: (v: string) => void; sub?: string;
}) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const [viewMonth, setViewMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const parsed = value.match(/^(\d{4}-\d{2}-\d{2})(?:\|(\d{4}-\d{2}-\d{2}))?$/);
  const startStr = parsed ? parsed[1] : "";
  const endStr = parsed && parsed[2] ? parsed[2] : "";
  const [hover, setHover] = useState<string>("");

  const pickDate = (dStr: string) => {
    if (!startStr || (startStr && endStr)) {
      onChange(dStr);
    } else if (dStr < startStr) {
      onChange(`${dStr}|${startStr}`);
    } else if (dStr === startStr) {
      onChange(dStr);
    } else {
      onChange(`${startStr}|${dStr}`);
    }
  };

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startWeekday = (firstDay.getDay() + 6) % 7;
  const daysInMonth = lastDay.getDate();
  const cells: (Date | null)[] = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);

  const inRange = (dStr: string): boolean => {
    if (!startStr) return false;
    if (endStr) return dStr >= startStr && dStr <= endStr;
    if (hover && startStr && !endStr) {
      const lo = hover < startStr ? hover : startStr;
      const hi = hover < startStr ? startStr : hover;
      return dStr >= lo && dStr <= hi;
    }
    return dStr === startStr;
  };

  return (
    <div style={{ marginBottom: 28 }}>
      <label style={{ display: "block", fontSize: 11, fontWeight: 700, marginBottom: 8, color: "#8a8a8a", letterSpacing: "0.12em" }}>{label}</label>
      {sub && <p style={{ fontSize: 12, color: "#8a8a8a", margin: "0 0 12px", textTransform: "none", letterSpacing: "0.02em" }}>{sub}</p>}

      <div style={{ border: "1px solid #e5e5e5", borderRadius: 0, padding: 18, background: "#fff" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <button type="button" onClick={() => setViewMonth(new Date(year, month - 1, 1))}
            style={{ border: "1px solid #e5e5e5", background: "#fff", borderRadius: 0, width: 32, height: 32, cursor: "pointer", fontFamily: "inherit", fontSize: 14 }}>‹</button>
          <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.05em", textTransform: "none" }}>
            {MONTHS_ES[month]} {year}
          </div>
          <button type="button" onClick={() => setViewMonth(new Date(year, month + 1, 1))}
            style={{ border: "1px solid #e5e5e5", background: "#fff", borderRadius: 0, width: 32, height: 32, cursor: "pointer", fontFamily: "inherit", fontSize: 14 }}>›</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 6 }}>
          {WEEKDAYS_ES.map(w => (
            <div key={w} style={{ textAlign: "center", fontSize: 10, fontWeight: 700, color: "#8a8a8a", letterSpacing: "0.08em", padding: "4px 0" }}>{w}</div>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
          {cells.map((d, i) => {
            if (!d) return <div key={i} />;
            const s = ymd(d);
            const isPast = d < today;
            const isStart = s === startStr;
            const isEnd = s === endStr;
            const selected = isStart || isEnd;
            const between = inRange(s) && !selected;
            return (
              <button key={i} type="button" disabled={isPast}
                onClick={() => pickDate(s)}
                onMouseEnter={() => setHover(s)}
                onMouseLeave={() => setHover("")}
                style={{
                  aspectRatio: "1",
                  border: "none",
                  borderRadius: 0,
                  background: selected ? "#000" : between ? "#e5e5e5" : "transparent",
                  color: selected ? "#fff" : isPast ? "#e5e5e5" : "#000",
                  fontSize: 13,
                  fontWeight: selected ? 700 : 400,
                  cursor: isPast ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                  transition: "background .1s",
                }}>
                {d.getDate()}
              </button>
            );
          })}
        </div>

        {startStr && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #e5e5e5", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, textTransform: "none", letterSpacing: "0.02em" }}>
            <span style={{ color: "#8a8a8a" }}>{fmtRange(startStr, endStr)}</span>
            <button type="button" onClick={() => onChange("")} style={{ border: "none", background: "transparent", color: "#c0392b", fontSize: 11, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.05em" }}>
              Limpiar
            </button>
          </div>
        )}
        {!startStr && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #e5e5e5", fontSize: 12, color: "#8a8a8a", textTransform: "none", letterSpacing: "0.02em" }}>
            Selecciona una fecha, o haz clic en dos para un rango
          </div>
        )}
      </div>
    </div>
  );
}

function PricePanel({ base, baseDays, mount, bracketSup, vanSup, cleaning, cyclorama, iva, total, subtotal, deposit, hasConvenir, step }: {
  base: number; baseDays: number; mount: number; bracketSup: { amount: number | null; label: string };
  vanSup: number; cleaning: number; cyclorama: number; iva: number | null; total: number | null;
  subtotal: number | null; deposit: number; hasConvenir: boolean; step: number;
}) {
  const fmt = (n: number) => n.toLocaleString("es-ES", { minimumFractionDigits: 2 });
  const hasBase = base > 0;

  return (
    <div style={{
      position: "sticky", top: 72,
      background: "#fff",
      borderTop: "2px solid #000",
      padding: "24px 4px 28px",
    }}>
      <p style={{ fontSize: 10, fontWeight: 700, color: "#8a8a8a", letterSpacing: "0.15em", margin: "0 0 20px" }}>RESUMEN</p>

      {!hasBase && (
        <p style={{ fontSize: 13, color: "#8a8a8a", textTransform: "none", letterSpacing: "0.02em" }}>
          Completa los pasos para ver el resumen de tu reserva.
        </p>
      )}

      {hasBase && (
        <div style={{ fontSize: 13 }}>
          <div style={{ padding: "8px 0", borderBottom: "1px solid #e5e5e5", color: "#8a8a8a", textTransform: "none", letterSpacing: "0.02em" }}>
            Alquiler espacio{baseDays > 1 ? ` × ${baseDays} días` : ""}
            {bracketSup.amount != null && bracketSup.amount > 0 && ` · ${bracketSup.label}`}
            {vanSup > 0 && ` · ${vanSup > 0 ? "vehículos de descarga" : ""}`}
          </div>
          {cleaning > 0 && (
            <div style={{ padding: "8px 0", borderBottom: "1px solid #e5e5e5", color: "#8a8a8a", textTransform: "none", letterSpacing: "0.02em" }}>
              Limpieza
            </div>
          )}
          {cyclorama > 0 && (
            <div style={{ padding: "8px 0", borderBottom: "1px solid #e5e5e5", color: "#8a8a8a", textTransform: "none", letterSpacing: "0.02em" }}>
              Pintado ciclorama
            </div>
          )}
          {mount > 0 && (
            <div style={{ padding: "8px 0", borderBottom: "1px solid #e5e5e5", color: "#8a8a8a", textTransform: "none", letterSpacing: "0.02em" }}>
              Montaje / desmontaje
            </div>
          )}

          {hasConvenir && (
            <div style={{ marginTop: 16, padding: "12px 14px", background: "#fff8ed", borderRadius: 0, fontSize: 12, color: "#c57300", textTransform: "none", letterSpacing: "0.02em" }}>
              Más de 50 personas — presupuesto a consultar
            </div>
          )}

          {step >= 1 && (
            <div style={{ marginTop: 20, padding: "14px 16px", background: "#fff", borderRadius: 0, border: "1px solid #e5e5e5", fontSize: 12, color: "#8a8a8a", textTransform: "none", letterSpacing: "0.02em", lineHeight: 1.6 }}>
              Recibirás el <strong style={{ color: "#000" }}>presupuesto detallado</strong> y las condiciones de alquiler en tu correo al completar el formulario.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CasaYubero() {
  const [section, setSection] = useState("home");
  const [lightbox, setLightbox] = useState<GalleryImage | null>(null);
  const [galleryPage, setGalleryPage] = useState(0);
  const [step, setStep] = useState(0);
  const [leadSent, setLeadSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [privacyDismissed, setPrivacyDismissed] = useState(() => {
    try { return localStorage.getItem("cy_privacy") === "1"; } catch { return false; }
  });
  const formRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);
  const galleryImages = galleryPages[galleryPage];

  const handleGalleryTouchStart = (e: React.TouchEvent) => { touchStartX.current = e.touches[0].clientX; };
  const handleGalleryTouchEnd = (e: React.TouchEvent) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) setGalleryPage(p => Math.min(p + 1, galleryPages.length - 1));
      else setGalleryPage(p => Math.max(p - 1, 0));
    }
  };

  const INITIAL_FORM: FormState = {
    sessionType: "", dayType: "", modality: "", mountDays: 0,
    peopleBracket: "", vans: 0,
    name: "", company: "", nif: "", email: "", phone: "", projectDesc: "", date: "",
    privacyConsent: false, marketingConsent: false, irpfRetention: false,
  };
  const [form, setForm] = useState<FormState>(INITIAL_FORM);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (section !== "form") {
      setForm(INITIAL_FORM);
      setStep(0);
      setLeadSent(false);
      setSendError("");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [section]);


  useEffect(() => {
    const handleHash = () => {
      const h = window.location.hash.replace("#", "");
      if (h === "form" || h === "presupuesto") setSection("form");
      else if (h === "galeria" || h === "gallery") setSection("gallery");
      else if (h === "servicios" || h === "services") setSection("services");
      else if (h === "faq") setSection("faq");
      else if (h === "ubicacion" || h === "location") setSection("location");
    };
    handleHash();
    window.addEventListener("hashchange", handleHash);
    return () => window.removeEventListener("hashchange", handleHash);
  }, []);

  const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const PHONE_RX = /^[+]?[\d\s().-]{7,}$/;
  const emailValid = EMAIL_RX.test(form.email);
  const phoneValid = PHONE_RX.test(form.phone);
  const emailError = form.email && !emailValid ? "Introduce un email válido (ej: nombre@dominio.com)" : "";
  const phoneError = form.phone && !phoneValid ? "Introduce un teléfono válido (mínimo 7 dígitos)" : "";

  const rangeDates = expandRange(form.date);
  const baseDays = Math.max(1, rangeDates.length || 1);
  const lvDays = rangeDates.filter(d => dayKindOf(d) === "lv").length;
  const sdfDays = rangeDates.filter(d => dayKindOf(d) === "sdf").length;
  const sdfDateList = rangeDates.filter(d => dayKindOf(d) === "sdf");
  const hasSDF = sdfDays > 0;

  const calcBase = (): number => {
    if (!form.modality) return 0;
    const rate = PRICES.base[form.modality];
    if (!form.date) return rate.lv * baseDays;
    return rate.lv * lvDays + rate.sdf * sdfDays;
  };

  // Montaje sin fecha asignada: tarifa laborable.
  const calcMount = (): number => {
    if (form.mountDays <= 0) return 0;
    return PRICES.base.jornada.lv * PRICES.mountDiscount * form.mountDays;
  };

  const calcBracketSup = () => PRICES.bracketSupplement(form.peopleBracket);
  const calcVanSup = (): number => PRICES.vanSupplement(form.vans);

  const hasConvenir = form.peopleBracket === "event";
  const base = calcBase();
  const mount = calcMount();
  const bracketSup = calcBracketSup();
  const vanSup = calcVanSup();
  const bracketSupAmount = bracketSup.amount ?? 0;
  const cleaning = form.modality ? PRICES.cleaning(form.peopleBracket) : 0;
  const cyclorama = form.modality ? PRICES.cycloramaPaint : 0;
  const subtotal = hasConvenir ? null : base + mount + bracketSupAmount + vanSup + cleaning + cyclorama;
  const iva = subtotal != null ? subtotal * PRICES.iva : null;
  const irpf = subtotal != null && form.irpfRetention ? subtotal * PRICES.irpf : null;
  const total = subtotal != null && iva != null ? subtotal + iva - (irpf || 0) : null;
  const deposit = PRICES.deposit(form.peopleBracket);
  const aforo = PRICES.aforo(form.peopleBracket);

  const canNext = (): boolean => {
    if (step === 0) return !!(form.sessionType && form.modality);
    if (step === 1) return !!form.peopleBracket;
    if (step === 2) return !!form.date;
    if (step === 3) return !!(form.name && emailValid && phoneValid && form.privacyConsent);
    return true;
  };

  const scrollToForm = () => {
    setSection("form");
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const modalityLabel: Record<string, string> = { jornada: "Jornada completa (8h+1)", media: "Media jornada (4h)" };
  const sessionLabels: Record<string, string> = { photo_video: "Foto / Vídeo", event: "Eventos", showroom: "Pop-up / Showroom", workshop: "Formación", photo: "Fotografía", video: "Vídeo", casting: "Casting" };

  const WHATSAPP_NUMBER = "34657864373";
  const WHATSAPP_MSG = encodeURIComponent("Hola Casa Yubero, me gustaría consultaros sobre el estudio.");

  const sendBudget = async (): Promise<boolean> => {
    if (sending || leadSent) return leadSent;
    setSending(true);
    setSendError("");
    try {
      const derivedDayType = sdfDays === 0 ? "lv" : (lvDays === 0 ? "sdf" : "mixed");
      const API = process.env.REACT_APP_API_URL || "http://localhost:3002";
      const r = await fetch(`${API}/api/budget/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, dayType: derivedDayType, lvDays, sdfDays, sdfDates: sdfDateList, base, cleaning, cyclorama, mount, vanSup, bracketSupAmount, subtotal, iva, irpf, irpfRate: PRICES.irpf, total, deposit, aforo }),
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(json.error || "No se ha podido enviar el presupuesto.");
      setLeadSent(true);
      return true;
    } catch (err: any) {
      setSendError(err.message || "Error de conexión. Inténtalo de nuevo en unos minutos.");
      return false;
    } finally {
      setSending(false);
    }
  };

  useFont();
  const isMobile = useIsMobile();
  const px = isMobile ? "16px" : "48px";

  useEffect(() => {
    document.body.style.overflow = isMobile && menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isMobile, menuOpen]);

  return (
    <div style={{ ...BASE_FONT, color: "#000", background: "#fff", minHeight: "100vh" }}>
      <Lightbox img={lightbox} onClose={() => setLightbox(null)} />

      {/* WHATSAPP FLOATING BUTTON */}
      <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MSG}`} target="_blank" rel="noopener noreferrer"
        aria-label="Contactar por WhatsApp"
        style={{
          position: "fixed", bottom: privacyDismissed ? 24 : 90, right: 24, zIndex: 100,
          width: 56, height: 56, borderRadius: "50%",
          background: "#25D366", color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center",
          textDecoration: "none", boxShadow: "0 4px 16px rgba(0,0,0,.2)",
        }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
        </svg>
      </a>

      {/* NAV */}
      <nav style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: `16px ${px}`, borderBottom: "1px solid #e5e5e5", position: "sticky", top: 0, background: "#fff", zIndex: 20 }}>
        <button style={{ fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", whiteSpace: "nowrap", background: "none", border: "none", padding: 0, fontFamily: "inherit", color: "inherit" }} onClick={() => { setSection("home"); setMenuOpen(false); }} aria-label="Casa Yubero — Inicio">Casa Yubero</button>
        {isMobile ? (
          <button
            onClick={() => setMenuOpen(o => !o)}
            aria-label={menuOpen ? "Cerrar menú" : "Abrir menú"}
            style={{ background: "none", border: "none", cursor: "pointer", padding: "6px 8px", display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            {menuOpen ? (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <line x1="4" y1="4" x2="16" y2="16" stroke="#000" strokeWidth="2" strokeLinecap="round"/>
                <line x1="16" y1="4" x2="4" y2="16" stroke="#000" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            ) : (
              <svg width="22" height="16" viewBox="0 0 22 16" fill="none" aria-hidden="true">
                <line x1="0" y1="1" x2="22" y2="1" stroke="#000" strokeWidth="2" strokeLinecap="round"/>
                <line x1="0" y1="8" x2="22" y2="8" stroke="#000" strokeWidth="2" strokeLinecap="round"/>
                <line x1="0" y1="15" x2="22" y2="15" stroke="#000" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            )}
          </button>
        ) : (
          <div style={{ display: "flex", gap: 32, fontSize: 12, fontWeight: 600, letterSpacing: "0.1em" }}>
            {([
              ["Espacio", "home", () => setSection("home")],
              ["Galería", "gallery", () => setSection("gallery")],
              ["Servicios", "services", () => setSection("services")],
              ["FAQ", "faq", () => setSection("faq")],
              ["Ubicación", "location", () => setSection("location")],
              ["Presupuesto", "form", scrollToForm],
            ] as [string, string, () => void][]).map(([label, id, go]) => (
              <button key={id} onClick={go} aria-current={section === id ? "page" : undefined}
                style={{ cursor: "pointer", opacity: section === id ? 1 : .45, background: "none", border: "none", padding: 0, font: "inherit", letterSpacing: "inherit", color: "inherit", textTransform: "inherit" }}>
                {label}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* MOBILE MENU OVERLAY */}
      {isMobile && menuOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 19, background: "#fff", display: "flex", flexDirection: "column", overflowY: "auto" }}>
          <div style={{ height: 57 }} />
          <div style={{ flex: 1, padding: "12px 24px 40px" }}>
            {([
              ["ESPACIO", "home"],
              ["GALERÍA", "gallery"],
              ["SERVICIOS", "services"],
              ["FAQ", "faq"],
              ["UBICACIÓN", "location"],
            ] as [string, string][]).map(([label, id]) => (
              <button key={id}
                onClick={() => { setSection(id); setMenuOpen(false); }}
                style={{ display: "block", width: "100%", background: "none", border: "none", borderBottom: "1px solid #e5e5e5", textAlign: "left", padding: "20px 0", fontSize: 24, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.05em", color: section === id ? "#000" : "#8a8a8a" }}>
                {label}
              </button>
            ))}
            <button
              onClick={() => { scrollToForm(); setMenuOpen(false); }}
              style={{ display: "block", width: "100%", marginTop: 32, padding: "18px 24px", background: "#fff", color: "#000", border: "1px solid #e5e5e5", borderRadius: 0, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.08em", textAlign: "center" }}>
              SOLICITAR PRESUPUESTO
            </button>
          </div>
        </div>
      )}

      {/* SECTION CONTENT — keyed for fade-in transition */}
      <div key={section} className="section-fade">

      {/* HERO */}
      {section === "home" && (
        <div>
          {/* H1 + SUBTÍTULO — sin imagen de cabecera, la página abre en texto (referencia La Mosca) */}
          <div style={{ padding: isMobile ? "36px 16px 0" : "56px 48px 0", maxWidth: 1100 }}>
            <h1 style={{ fontSize: isMobile ? 34 : 60, fontWeight: 700, lineHeight: 1.03, margin: "0 0 22px", letterSpacing: isMobile ? -1.5 : -2.5, textTransform: "uppercase" }}>
              Estudio de fotografía y vídeo en Madrid
            </h1>
            <p style={{ fontSize: 12, color: "#8a8a8a", margin: 0, textTransform: "uppercase", letterSpacing: "0.08em" }}>Calle Abolengo 12, Carabanchel, Madrid</p>
          </div>

          {/* FICHA TÉCNICA + FOTOS — specs fijas a un lado, horizontales agrupadas arriba
              y verticales en rejilla debajo, para compensar el peso visual */}
          <div style={{ padding: isMobile ? "40px 16px 64px" : "48px 48px 80px", borderBottom: `1px solid ${T.rule}` }}>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "300px 1fr", gap: isMobile ? 32 : 56, alignItems: "start" }}>
              <div style={{ position: isMobile ? "static" : "sticky", top: 96 }}>
                {[
                  ["Superficie útil", "190 m²"],
                  ["Altura libre", "6,50 m"],
                  ["Ciclorama", "5 × 5 m, blanco"],
                  ["Suelo", "Hormigón pulido, carga máx. 500 kg/m²"],
                  ["Climatización", "Suelo radiante y refrescante, 5 zonas"],
                  ["Luz natural", "Patio interior + fachada, orientación oeste"],
                  ["Insonorización", "Apta para vídeo, ruido ambiente bajo"],
                  ["Electricidad", "35 tomas Schuko, potencia para producción"],
                  ["Conectividad", "Fibra simétrica 750 Mb"],
                  ["Load-in", "Puerta 2,90 × 2,20 m, sin escalones"],
                  ["Cocina", "Cocina profesional equipada + menaje"],
                  ["Espacios", "Sala de procesos, changing room, 2 aseos"],
                  ["Mesa comunal", "10 plazas"],
                  ["Patio", "Jardín interior + ducha exterior"],
                  ["Tarifa", "Desde 490 €/jornada"],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "11px 0", borderTop: `1px solid ${T.rule}`, fontSize: 13, textTransform: "none", letterSpacing: "0.01em" }}>
                    <span style={{ color: T.mute }}>{k}</span>
                    <span style={{ color: T.ink, textAlign: "right" }}>{v}</span>
                  </div>
                ))}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? 24 : 32 }}>
                {SIMONE_GALLERY.filter(img => img.wide).map((img, i) => (
                  <figure key={img.src} style={{ margin: 0, cursor: "zoom-in" }} onClick={() => setLightbox(img)}>
                    <ImgWithFallback src={img.src} alt={img.alt} eager={i === 0} width={1600} style={{ width: "100%", aspectRatio: "4 / 3", objectFit: "cover", display: "block" }} />
                  </figure>
                ))}
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, 1fr)", gap: isMobile ? 10 : 16 }}>
                  {SIMONE_GALLERY.filter(img => !img.wide).map(img => (
                    <figure key={img.src} style={{ margin: 0, cursor: "zoom-in" }} onClick={() => setLightbox(img)}>
                      <ImgWithFallback src={img.src} alt={img.alt} width={900} style={{ width: "100%", aspectRatio: "3 / 4", objectFit: "cover", display: "block" }} />
                    </figure>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* PUERTA MODULAR */}
          <div style={{ marginTop: isMobile ? 56 : 80 }}>
            <ModularDoor images={MODULAR_DOOR_IMAGES} isMobile={isMobile} />
          </div>

          <div style={{ padding: isMobile ? "32px 16px 64px" : "40px 48px 80px" }}>
            <button onClick={() => setSection("gallery")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", padding: 0, fontFamily: "inherit", marginRight: 28 }}>
              VER TODAS LAS FOTOS →
            </button>
            <button onClick={scrollToForm} style={{ padding: "15px 36px", background: "#fff", color: "#000", border: "1px solid #e5e5e5", fontSize: 13, fontWeight: 700, cursor: "pointer", letterSpacing: "0.08em" }}>Solicitar presupuesto</button>
          </div>

          {/* TRABAJOS — prueba social */}
          <div style={{ paddingBottom: 80 }}>
            <div style={{ padding: isMobile ? "0 16px" : "0 48px", marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", color: "#8a8a8a" }}>TRABAJOS</div>
            </div>
            <ImgWithFallback
              src="https://res.cloudinary.com/dre2qllr2/image/upload/v1780599330/cy_22_05_2026_0021.jpg"
              alt="Rodaje de moda en Casa Yubero — equipo de producción frente al ciclorama"
              width={1800}
              style={{ width: "100%", height: isMobile ? 300 : 520, objectFit: "cover", display: "block" }}
            />
            <div style={{ padding: isMobile ? "12px 16px 0" : "12px 48px 0", fontSize: 12, color: "#8a8a8a", letterSpacing: "0.08em" }}>
              RODAJE DE MODA — MAYO 2026
            </div>
          </div>

          {/* CÓMO LLEGAR */}
          <div style={{ padding: `0 ${px} 80px`, maxWidth: 1100 }}>
            <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 700, margin: "0 0 8px", textTransform: "none", letterSpacing: -0.5 }}>Cómo llegar</h2>
            <p style={{ fontSize: 13, color: "#8a8a8a", margin: "0 0 28px", textTransform: "none", letterSpacing: "0.02em" }}>Calle Abolengo 12, 28025 Carabanchel, Madrid.</p>

            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 24, alignItems: "stretch" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <ImgWithFallback
                  src="https://res.cloudinary.com/dre2qllr2/image/upload/v1780599322/cy_22_05_2026_0012.jpg"
                  alt="Fachada de Casa Yubero en Calle Abolengo 12, Carabanchel"
                  width={1200}
                  style={{ width: "100%", height: 220, objectFit: "cover", borderRadius: 0, border: "1px solid #e5e5e5", display: "block" }}
                />
                <div style={{ flex: 1, minHeight: 320, borderRadius: 0, overflow: "hidden", border: "1px solid #e5e5e5" }}>
                  <iframe
                    title="Ubicación Casa Yubero"
                    src="https://www.google.com/maps?q=Calle+Abolengo+12,+28025+Madrid&output=embed"
                    width="100%" height="100%"
                    style={{ border: 0, display: "block", minHeight: 320 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {[
                  { t: "Metro", v: "Vista Alegre (L5) — 1 min a pie · Oporto (L5) — 7 min · Carabanchel (L5) también próximo" },
                  { t: "Bus", v: "Líneas 34, 35, 60, 108, 118 — parada Calle Abolengo" },
                  { t: "Cercanías", v: "Estación Laguna (C-5) — 12 min andando" },
                  { t: "Coche / camión", v: "Acceso directo desde M-30 (salida 32 Carabanchel). Carga y descarga frente a fachada." },
                  { t: "Parking público", v: "Parking de Vista Alegre a pocos minutos a pie · El acceso de fachada admite turismos y furgonetas medianas." },
                  { t: "Zona ZBE", v: "Fuera de Madrid Central. Acceso libre para todos los vehículos." },
                ].map(item => (
                  <div key={item.t} style={{ padding: "14px 18px", border: "1px solid #e5e5e5", borderRadius: 0, background: "#fff" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", color: "#8a8a8a", marginBottom: 4 }}>{item.t.toUpperCase()}</div>
                    <div style={{ fontSize: 13, color: "#8a8a8a", lineHeight: 1.5, textTransform: "none", letterSpacing: "0.02em" }}>{item.v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* GALLERY */}
      {section === "gallery" && (
        <div style={{ padding: isMobile ? "16px" : "48px" }}>
          <button onClick={() => setSection("home")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#8a8a8a", fontFamily: "inherit", letterSpacing: "0.05em", padding: 0, marginBottom: 28 }}>← Volver</button>

          <div style={{ maxWidth: 720, marginBottom: 28 }}>
            {galleryPage === 0 && (
              <>
                <p style={{ fontSize: 15, color: "#8a8a8a", lineHeight: 1.7, margin: "0 0 10px", textTransform: "none", letterSpacing: "0.01em" }}>
                  Fotografía de estudio por Simone Marcolin.
                </p>
                <p style={{ fontSize: 13, color: "#8a8a8a", margin: "0 0 8px", textTransform: "none", letterSpacing: "0.02em" }}>
                  Arquitectura: <a href="https://www.ba-rro.com" target="_blank" rel="noopener noreferrer" style={{ color: "#8a8a8a", fontWeight: 700, textDecoration: "underline" }}>estudio ba-rro</a>.
                </p>
              </>
            )}
            {galleryPage === 1 && (
              <p style={{ fontSize: 15, color: "#8a8a8a", lineHeight: 1.7, margin: "0 0 10px", textTransform: "none", letterSpacing: "0.01em" }}>
                Antiguo taller de motos rehabilitado en estilo brutalista industrial. 190 m² de nave diáfana con 6,50 m de altura libre, ciclorama de 5×5 y luz natural, pensado para producción de fotografía y vídeo.
              </p>
            )}
            {galleryPage === 2 && (
              <>
                <p style={{ fontSize: 15, color: "#8a8a8a", lineHeight: 1.7, margin: "0 0 10px", textTransform: "none", letterSpacing: "0.01em" }}>
                  Así era el espacio antes de la rehabilitación: un antiguo taller mecánico de motos en Carabanchel. La obra ha conservado la estructura original — muros de hormigón, vigas vistas y huecos de gran formato — y la ha puesto al servicio de la producción audiovisual.
                </p>
                <p style={{ fontSize: 13, color: "#8a8a8a", margin: "0 0 8px", textTransform: "none", letterSpacing: "0.02em" }}>
                  Reforma integral ejecutada por <strong style={{ color: "#8a8a8a" }}>Grupo Sirova</strong>, bajo la dirección de <strong style={{ color: "#8a8a8a" }}>Rodrigo Simón</strong>, administrador de la compañía.
                </p>
              </>
            )}
            <p style={{ fontSize: 12, color: "#8a8a8a", margin: 0, textTransform: "none", letterSpacing: "0.02em" }}>Haz clic en una imagen para ampliarla.</p>
          </div>

          {galleryPage === 0 ? (
            <div onTouchStart={handleGalleryTouchStart} onTouchEnd={handleGalleryTouchEnd} style={{ display: "flex", flexDirection: "column", gap: isMobile ? 24 : 40 }}>
              {(() => {
                const wide = SIMONE_GALLERY.filter(img => img.wide);
                const pairsSrc = SIMONE_GALLERY.filter(img => !img.wide);
                const pairs: GalleryImage[][] = [];
                for (let i = 0; i < pairsSrc.length; i += 2) pairs.push(pairsSrc.slice(i, i + 2));
                return (
                  <>
                    {wide.map(img => (
                      <figure key={img.src} style={{ margin: 0, cursor: "zoom-in" }} onClick={() => setLightbox(img)}>
                        <ImgWithFallback src={img.src} alt={img.alt} eager width={3000} style={{ width: "100%", aspectRatio: "4 / 3", objectFit: "cover", display: "block" }} />
                      </figure>
                    ))}
                    {pairs.map((pair, pi) => (
                      <div key={pi} style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? 24 : 20 }}>
                        {pair.map(img => (
                          <figure key={img.src} style={{ margin: 0, cursor: "zoom-in" }} onClick={() => setLightbox(img)}>
                            <ImgWithFallback src={img.src} alt={img.alt} width={2250} style={{ width: "100%", aspectRatio: "3 / 4", objectFit: "cover", display: "block" }} />
                          </figure>
                        ))}
                      </div>
                    ))}
                  </>
                );
              })()}
            </div>
          ) : isMobile ? (
            <div onTouchStart={handleGalleryTouchStart} onTouchEnd={handleGalleryTouchEnd}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {galleryImages.map((img, i) => (
                  <div key={`${galleryPage}-${i}`} onClick={() => setLightbox(img)} style={{ cursor: "zoom-in", overflow: "hidden", aspectRatio: "1" }}>
                    <ImgWithFallback src={img.src} alt={img.alt} width={600} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            (() => {
              const rowPatterns = [
                [3, 2],
                [1, 1, 1],
                [2, 1, 2],
                [1, 3],
                [1, 1, 1],
                [2, 1],
              ];
              const rowHeights = [400, 280, 360, 340, 260, 380];
              const rows: GalleryImage[][] = [];
              let idx = 0;
              let patternIdx = 0;
              while (idx < galleryImages.length) {
                const pattern = rowPatterns[patternIdx % rowPatterns.length];
                rows.push(galleryImages.slice(idx, idx + pattern.length));
                idx += pattern.length;
                patternIdx++;
              }
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {rows.map((row, ri) => {
                    const pattern = rowPatterns[ri % rowPatterns.length];
                    const h = rowHeights[ri % rowHeights.length];
                    return (
                      <div key={ri} style={{ display: "flex", gap: 4, height: h }}>
                        {row.map((img, ci) => (
                          <div key={img.src} onClick={() => setLightbox(img)} style={{ flex: pattern[ci] ?? 1, overflow: "hidden", cursor: "zoom-in" }}>
                            <ImgWithFallback src={img.src} alt={img.alt} width={1200} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              );
            })()
          )}

          {/* Paginación textual */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 40, paddingTop: 20, borderTop: "1px solid #e5e5e5", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em" }}>
            <button onClick={() => setGalleryPage(p => Math.max(0, p - 1))} disabled={galleryPage === 0}
              style={{ background: "none", border: "none", cursor: galleryPage === 0 ? "default" : "pointer", opacity: galleryPage === 0 ? .3 : 1, fontFamily: "inherit", letterSpacing: "inherit", padding: 0, color: "#000" }}>
              ← Anterior
            </button>
            <span style={{ color: "#8a8a8a" }}>{galleryPage + 1} / {galleryPages.length}</span>
            <button onClick={() => setGalleryPage(p => Math.min(galleryPages.length - 1, p + 1))} disabled={galleryPage === galleryPages.length - 1}
              style={{ background: "none", border: "none", cursor: galleryPage === galleryPages.length - 1 ? "default" : "pointer", opacity: galleryPage === galleryPages.length - 1 ? .3 : 1, fontFamily: "inherit", letterSpacing: "inherit", padding: 0, color: "#000" }}>
              Siguiente →
            </button>
          </div>

        </div>
      )}

      {/* RATES */}
      {/* SERVICES */}
      {section === "services" && (
        <div style={{ padding: isMobile ? "16px" : "48px", maxWidth: 920 }}>
          <button onClick={() => setSection("home")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#8a8a8a", fontFamily: "inherit", letterSpacing: "0.05em", padding: 0, marginBottom: 28 }}>← Volver</button>
          <h2 style={{ fontSize: isMobile ? 28 : 44, fontWeight: 700, margin: "0 0 8px", textTransform: "none", letterSpacing: -1 }}>Servicios</h2>
          <p style={{ fontSize: 14, color: "#8a8a8a", margin: "0 0 40px", textTransform: "none", letterSpacing: "0.02em", lineHeight: 1.6 }}>
            Lo que está incluido en cada reserva y los servicios extras que podemos añadir a tu producción.
          </p>

          {/* TIPOS DE USO */}
          <div style={{ marginBottom: 52 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", color: "#8a8a8a", marginBottom: 16 }}>TIPOS DE USO</div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 10 }}>
              {[
                { label: "Casting", desc: "Sala de casting con ciclorama y changing room", href: "/casting-madrid/" },
                { label: "Fotografía de producto", desc: "Sets configurables para e-commerce y catálogo", href: "/fotografia-producto-madrid/" },
                { label: "Fotografía corporativa", desc: "Headshots, retratos de equipo y contenido de empresa", href: "/fotografia-corporativa-madrid/" },
                { label: "Spot publicitario", desc: "Nave con 6,50 m de altura y acceso de trucks", href: "/spot-publicitario-madrid/" },
                { label: "Showroom", desc: "Espacio diáfano para colecciones y presentaciones a prensa", href: "/showroom-madrid/" },
                { label: "Pop-up store", desc: "Tienda temporal con acceso de vehículos y cocina", href: "/pop-up-madrid/" },
                { label: "Cursos y talleres", desc: "Hasta 50 personas con cocina profesional y fibra 750 Mb", href: "/espacio-cursos-talleres-madrid/" },
                { label: "Presentaciones", desc: "Lanzamientos de marca y eventos corporativos", href: "/alquiler-espacio-presentacion-madrid/" },
              ].map(({ label, desc, href }) => (
                <a key={href} href={href} target="_blank" rel="noopener noreferrer" className="uso-card">
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, textTransform: "none", letterSpacing: "-0.1px", marginBottom: 3 }}>{label}</div>
                    <div className="uso-card-desc" style={{ fontSize: 12, color: "#8a8a8a", textTransform: "none", letterSpacing: "0.01em", lineHeight: 1.4 }}>{desc}</div>
                  </div>
                  <span className="uso-card-arrow" style={{ fontSize: 18, color: "#ccc", flexShrink: 0 }}>→</span>
                </a>
              ))}
            </div>
          </div>

          {/* INCLUDED — Espacios y dotación */}
          <div style={{ marginBottom: 40 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", color: "#1a7a40", marginBottom: 14 }}>ESPACIOS Y DOTACIÓN INCLUIDOS</div>
            {[
              ["Sala de procesos", "Mesa de trabajo con dos sillas para modelos y zona de descanso aislada del set, ideal para retoques, esperas y revisión de tomas."],
              ["Changing room privado", "Vestidor con aseo individual integrado y burro con perchas, exclusivo para cambios de vestuario y maquillaje."],
              ["Dos aseos en el espacio", "Aseo independiente para el equipo más el aseo privado del changing room — sin colas durante el rodaje."],
              ["Cocina profesional equipada", "Cocina y horno, extractor industrial, fregadero amplio y menaje. Lista para catering, recetas a cámara o food styling."],
              ["Mesa comunal de 10 plazas", "Punto de reunión para briefings, comida de equipo o coworking durante el rodaje."],
              ["Patio ajardinado", "Jardín interior con vegetación y ducha exterior. Segunda localización o zona de pausa al aire libre."],
              ["WiFi alta velocidad + instalación eléctrica para producción", "Fibra simétrica 750 Mb para envío de archivos pesados, monitores y streaming. 35 enchufes Schuko distribuidos por todo el espacio, regletas y alargadores en préstamo."],
            ].map(([name, desc], i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "baseline", padding: "20px 0", borderBottom: "1px solid #e5e5e5", gap: 24, textTransform: "none", letterSpacing: "0.01em" }}>
                <div>
                  <div style={{ fontSize: 17, color: "#000", marginBottom: 4 }}>{name}</div>
                  <div style={{ fontSize: 13, color: "#8a8a8a", lineHeight: 1.5 }}>{desc}</div>
                </div>
                <span style={{ fontWeight: 600, color: "#1a7a40", fontSize: 13 }}>incl.</span>
              </div>
            ))}
          </div>

          {/* INCLUDED — Servicios operativos */}
          <div style={{ marginBottom: 48 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", color: "#1a7a40", marginBottom: 14 }}>SERVICIOS OPERATIVOS INCLUIDOS</div>
            {[
              ["Limpieza", "Limpieza completa del espacio antes y después de tu reserva."],
              ["Pintado de ciclorama", "Ciclorama recién pintado para garantizar un blanco perfecto."],
            ].map(([name, desc], i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "baseline", padding: "20px 0", borderBottom: "1px solid #e5e5e5", gap: 24, textTransform: "none", letterSpacing: "0.01em" }}>
                <div>
                  <div style={{ fontSize: 17, color: "#000", marginBottom: 4 }}>{name}</div>
                  <div style={{ fontSize: 13, color: "#8a8a8a", lineHeight: 1.5 }}>{desc}</div>
                </div>
                <span style={{ fontWeight: 600, color: "#1a7a40", fontSize: 13 }}>incl.</span>
              </div>
            ))}
          </div>

          {/* EXTRAS */}
          {[
            {
              title: "Servicios extras — Producción",
              rows: [
                ["Catering", "Servicio de comida y bebida para tu equipo durante el rodaje"],
                ["Alquiler de mobiliario", "Mesas, sillas, sofás y atrezzo según las necesidades del set"],
                ["Oficina privada", "Espacio reservado para producción, agencia o cliente"],
              ],
            },
            {
              title: "Servicios extras — Material técnico",
              rows: [
                ["Alquiler de material de foto", "Cámaras, ópticas, trípodes y accesorios fotográficos"],
                ["Alquiler de material audiovisual", "Iluminación, sonido, monitores y equipo de vídeo"],
              ],
            },
          ].map((group, gi) => (
            <div key={gi} style={{ marginBottom: 36 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", color: "#8a8a8a", marginBottom: 14 }}>{group.title.toUpperCase()}</div>
              {group.rows.map(([name, desc], i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "baseline", padding: "20px 0", borderBottom: "1px solid #e5e5e5", gap: 24, textTransform: "none", letterSpacing: "0.01em" }}>
                  <div>
                    <div style={{ fontSize: 17, color: "#000", marginBottom: 4 }}>{name}</div>
                    <div style={{ fontSize: 13, color: "#8a8a8a", lineHeight: 1.5 }}>{desc}</div>
                  </div>
                  <span style={{ fontWeight: 600, color: "#8a8a8a", fontSize: 14 }}>a consultar</span>
                </div>
              ))}
            </div>
          ))}

          <div style={{ fontSize: 13, color: "#8a8a8a", lineHeight: 1.8, textTransform: "none", letterSpacing: "0.02em", marginTop: 8, marginBottom: 48 }}>
            <p style={{ margin: 0 }}>Trabajamos con proveedores de confianza en Madrid. Te ofrecemos opciones según el alcance de tu producción y presupuesto.</p>
          </div>

          {/* CTA */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button onClick={scrollToForm} style={{ padding: "15px 36px", background: "#fff", color: "#000", border: "1px solid #e5e5e5", borderRadius: 0, fontSize: 13, fontWeight: 700, cursor: "pointer", letterSpacing: "0.08em" }}>
              Solicitar presupuesto
            </button>
          </div>
        </div>
      )}

      {/* FAQ */}
      {section === "faq" && (
        <div style={{ padding: isMobile ? "16px" : "48px", maxWidth: 920 }}>
          <button onClick={() => setSection("home")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#8a8a8a", fontFamily: "inherit", letterSpacing: "0.05em", padding: 0, marginBottom: 28 }}>← Volver</button>
          <h2 style={{ fontSize: isMobile ? 28 : 44, fontWeight: 700, margin: "0 0 8px", textTransform: "none", letterSpacing: -1 }}>Preguntas frecuentes</h2>
          <p style={{ fontSize: 14, color: "#8a8a8a", margin: "0 0 40px", textTransform: "none", letterSpacing: "0.02em", lineHeight: 1.6 }}>
            Lo más preguntado sobre rodajes en Casa Yubero. Si no encuentras tu respuesta, escríbenos por WhatsApp o desde el formulario de presupuesto.
          </p>

          {[
            {
              title: "El espacio",
              items: [
                ["¿Casa Yubero tiene oscuro total (black-out)?", "No. Casa Yubero es un estudio de luz natural, con ventanales en los tres lados del patio interior (7 hojas móviles) y dos ventanales en fachada de 3+3 hojas. No disponemos de cortinas opacas ni black-out completo: si tu rodaje requiere oscuridad total, este no es el espacio adecuado."],
                ["¿Cómo se controla la luz natural?", "La orientación oeste, las hojas móviles del patio interior y los ventanales de fachada permiten regular la entrada de luz a lo largo del día — luz suave por la mañana y luz cálida directa por la tarde. Para difuminarla puedes traer banderas, difusores o telas; el techo permite fijar cargas en viguetas y cercha de hormigón vista."],
                ["¿Qué tipo de climatización tenéis?", "Suelo radiante y refrescante distribuido en 5 zonas independientes, cada una con su termostato regulable a gusto del cliente. No usamos aire acondicionado tradicional: el confort térmico es uniforme y silencioso, sin ruido ni corrientes — ideal para rodajes de vídeo."],
                ["¿Hay sistema de insonorización?", "El espacio tiene un nivel de ruido ambiente bajo, apto para grabación de vídeo en condiciones normales. No es una sala anecoica ni un estudio de sonido profesional."],
              ],
            },
            {
              title: "Reservas y cancelaciones",
              items: [
                ["¿Cómo se reserva el estudio?", "Solicita presupuesto desde la web, te lo confirmamos en menos de 24 horas. La reserva se cierra con el pago del 100% más la fianza, mínimo 7 días naturales antes del rodaje."],
                ["¿Cuál es la política de cancelación?", "Cancelaciones por escrito con más de 14 días de antelación: reembolso del importe abonado menos una comisión de gestión de 30 €. Entre 14 y 7 días antes: reembolso del 75%. Entre 7 días y 48 horas antes: reembolso del 50%. Con menos de 48 horas: sin reembolso. La fianza se devuelve íntegramente en todos los casos si no hay daños imputables al cliente."],
                ["¿Puedo modificar la fecha después de reservar?", "Sí, una modificación gratuita siempre que la nueva fecha esté disponible y se solicite con más de 7 días de antelación. Cambios posteriores tienen una comisión del 20%."],
                ["¿Cuándo se devuelve la fianza?", "En un plazo máximo de 5 días hábiles tras el rodaje, una vez verificado que el espacio se entrega en las mismas condiciones."],
                ["¿Tenéis seguro de responsabilidad civil?", "Sí, el estudio está cubierto por una póliza de RC. Recomendamos al cliente contar con su propia RC para producciones grandes."],
              ],
            },
            {
              title: "Durante el rodaje",
              items: [
                ["¿Se puede fumar dentro del estudio?", "No se permite fumar en interior. Disponemos de patio ajardinado donde sí está permitido."],
                ["¿Aceptáis mascotas en sesiones?", "Sí, siempre que vengan controladas y bajo responsabilidad del cliente. Avísanos al reservar si vienen animales para preparar el espacio."],
                ["¿Hay límite de horario o decibelios?", "El horario habitual es de 08:00 a 22:00. Para rodajes nocturnos o eventos que excedan, se aplica tarifa de hora extra nocturna. Por respeto al vecindario, se restringe ruido elevado a partir de las 22:00."],
                ["¿Se puede usar fuego, humo o agua en el set?", "Sí, con aviso previo. Disponemos de extintores y sistema de ventilación. Para escenas con mucha agua o líquidos pesados, consúltanos antes."],
                ["¿Hay límite de personas en el espacio?", "Producciones de hasta 50 personas se presupuestan directamente desde el formulario, con el suplemento de producción correspondiente a cada tramo. Para más de 50 personas o eventos, escríbenos y te preparamos una propuesta a medida con los permisos necesarios."],
              ],
            },
            {
              title: "Logística y producción",
              items: [
                ["¿Dónde se aparca el camión de producción?", "Disponemos de fachada con load-in directo (puerta 2,90 × 2,20 m). Para camiones grandes que necesiten estacionar en calle es necesario reservar plazas a través de la Comunidad de Madrid (gestión a cargo del cliente)."],
                ["¿Qué incluye exactamente el pintado de ciclorama?", "Pintado completo del ciclorama 5×5 con pintura blanca de calidad foto-vídeo, dejando el set listo el día del rodaje. Incluido en todas las reservas."],
                ["¿Puedo hacer una visita previa al espacio?", "Sí, totalmente recomendable. Concertamos visitas de lunes a viernes en horario comercial. Solicítala desde el formulario o por WhatsApp."],
                ["¿Tenéis WiFi de alta velocidad?", "Sí, fibra simétrica de 750 Mb disponible en todo el espacio."],
                ["¿Hay catering / servicio de comida?", "Trabajamos con caterings de confianza en Madrid. Consulta opciones en la sección Servicios o solicítalo en el formulario."],
                ["¿Puedo dejar material guardado entre días de rodaje?", "Sí, para rodajes multi-día el material puede quedarse en el estudio bajo responsabilidad del cliente. El estudio se cierra con alarma."],
              ],
            },
          ].map(group => (
            <div key={group.title} style={{ marginBottom: 36 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", color: "#8a8a8a", marginBottom: 12 }}>{group.title.toUpperCase()}</div>
              {group.items.map(([q, a], i) => (
                <details key={i} style={{ borderBottom: "1px solid #e5e5e5", padding: "16px 0", textTransform: "none", letterSpacing: "0.02em" }}>
                  <summary style={{ fontSize: 15, fontWeight: 600, color: "#000", cursor: "pointer", listStyle: "none", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                    <span>{q}</span>
                    <span style={{ fontSize: 18, color: "#8a8a8a", fontWeight: 300 }}>+</span>
                  </summary>
                  <p style={{ fontSize: 14, color: "#8a8a8a", lineHeight: 1.7, margin: "12px 0 0" }}>{a}</p>
                </details>
              ))}
            </div>
          ))}

          <div style={{ marginTop: 40, padding: "20px 24px", border: "1px solid #e5e5e5", borderRadius: 0, background: "#fff", textTransform: "none", letterSpacing: "0.02em" }}>
            <p style={{ fontSize: 13, color: "#8a8a8a", margin: "0 0 12px", lineHeight: 1.6 }}>¿Tienes otra pregunta?</p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MSG}`} target="_blank" rel="noopener noreferrer"
                style={{ padding: "12px 24px", background: "#25D366", color: "#fff", borderRadius: 0, fontSize: 13, fontWeight: 700, textDecoration: "none", letterSpacing: "0.08em" }}>
                WhatsApp
              </a>
              <button onClick={scrollToForm} style={{ padding: "12px 24px", background: "#fff", color: "#000", border: "1px solid #e5e5e5", borderRadius: 0, fontSize: 13, fontWeight: 700, cursor: "pointer", letterSpacing: "0.08em" }}>
                Solicitar presupuesto
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PRIVACY POLICY */}
      {section === "privacy" && (
        <div style={{ padding: isMobile ? "16px" : "48px", maxWidth: 780 }}>
          <h2 style={{ fontSize: 36, fontWeight: 700, margin: "0 0 8px", textTransform: "none", letterSpacing: -0.5 }}>Política de privacidad</h2>
          <p style={{ fontSize: 13, color: "#8a8a8a", margin: "0 0 40px", textTransform: "none", letterSpacing: "0.02em" }}>
            Última actualización: 1 de mayo de 2026
          </p>

          <div style={{ fontSize: 14, color: "#8a8a8a", lineHeight: 1.8, textTransform: "none", letterSpacing: "0.02em" }}>
            <h3 style={{ fontSize: 15, color: "#000", margin: "0 0 8px", letterSpacing: "0.05em", textTransform: "uppercase" }}>1. Responsable del tratamiento</h3>
            <p style={{ margin: "0 0 28px" }}>
              Casa Yubero — Calle Abolengo 12, 28025 Madrid (España).<br />
              Contacto: <a href="mailto:hello@casayubero.com" style={{ color: "#000" }}>hello@casayubero.com</a> · +34 657 864 373.
            </p>

            <h3 style={{ fontSize: 15, color: "#000", margin: "0 0 8px", letterSpacing: "0.05em", textTransform: "uppercase" }}>2. Datos que recogemos</h3>
            <p style={{ margin: "0 0 28px" }}>
              Recogemos los datos que tú mismo nos facilitas mediante el formulario de presupuesto: nombre, empresa (opcional), NIF/CIF (opcional), email, teléfono, fechas y descripción de tu producción. No utilizamos cookies de seguimiento ni pixels publicitarios.
            </p>

            <h3 style={{ fontSize: 15, color: "#000", margin: "0 0 8px", letterSpacing: "0.05em", textTransform: "uppercase" }}>3. Finalidades y base jurídica</h3>
            <p style={{ margin: "0 0 12px" }}>
              <strong>(a) Gestión del presupuesto y reserva</strong> — base jurídica: ejecución de medidas precontractuales y contractuales (art. 6.1.b RGPD). Sin estos datos no podemos elaborar ni enviarte el presupuesto.
            </p>
            <p style={{ margin: "0 0 12px" }}>
              <strong>(b) Cumplimiento de obligaciones contables y fiscales</strong> — base jurídica: cumplimiento de obligación legal (art. 6.1.c RGPD).
            </p>
            <p style={{ margin: "0 0 28px" }}>
              <strong>(c) Comunicaciones comerciales</strong> (eventos, fechas exclusivas, descuentos) — base jurídica: tu consentimiento expreso (art. 6.1.a RGPD y art. 21 LSSI-CE), prestado mediante la casilla específica del formulario. Es un consentimiento independiente del anterior y revocable en cualquier momento sin que afecte a la gestión de tu reserva.
            </p>

            <h3 style={{ fontSize: 15, color: "#000", margin: "0 0 8px", letterSpacing: "0.05em", textTransform: "uppercase" }}>4. Plazos de conservación</h3>
            <p style={{ margin: "0 0 28px" }}>
              Datos de presupuesto y contractuales: durante la relación comercial y los plazos legales de conservación contable (hasta 6 años, art. 30 Cód. Comercio). Datos para marketing: hasta que retires el consentimiento.
            </p>

            <h3 style={{ fontSize: 15, color: "#000", margin: "0 0 8px", letterSpacing: "0.05em", textTransform: "uppercase" }}>5. Destinatarios</h3>
            <p style={{ margin: "0 0 28px" }}>
              Trabajamos con encargados del tratamiento (art. 28 RGPD) que prestan servicios técnicos: <strong>Resend, Inc.</strong> (envío de email transaccional, garantías DPF UE-EE.UU.) y proveedores de alojamiento. No vendemos ni cedemos datos a terceros con fines publicitarios.
            </p>

            <h3 style={{ fontSize: 15, color: "#000", margin: "0 0 8px", letterSpacing: "0.05em", textTransform: "uppercase" }}>6. Tus derechos</h3>
            <p style={{ margin: "0 0 28px" }}>
              Puedes ejercer en cualquier momento los derechos de <strong>acceso, rectificación, supresión, oposición, limitación y portabilidad</strong> escribiendo a <a href="mailto:hello@casayubero.com" style={{ color: "#000" }}>hello@casayubero.com</a> indicando "RGPD" en el asunto. También puedes <strong>retirar tu consentimiento</strong> de marketing en cualquier momento desde el enlace de baja incluido en cada correo. Si consideras que hemos vulnerado tus derechos, puedes presentar reclamación ante la <strong>Agencia Española de Protección de Datos</strong> (<a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" style={{ color: "#000" }}>www.aepd.es</a> · C/ Jorge Juan 6, 28001 Madrid).
            </p>

            <h3 style={{ fontSize: 15, color: "#000", margin: "0 0 8px", letterSpacing: "0.05em", textTransform: "uppercase" }}>7. Seguridad</h3>
            <p style={{ margin: "0 0 28px" }}>
              Aplicamos medidas técnicas y organizativas razonables para proteger tus datos: cifrado en tránsito (HTTPS), acceso restringido al panel de gestión y registro de prueba del consentimiento (timestamp + IP) como exige el RGPD.
            </p>
          </div>

          <div style={{ marginTop: 32, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button onClick={() => setSection("home")}
              style={{ padding: "12px 24px", background: "#000", color: "#fff", border: "none", borderRadius: 0, fontSize: 13, fontWeight: 700, cursor: "pointer", letterSpacing: "0.08em", fontFamily: "inherit" }}>
              ← Volver al inicio
            </button>
          </div>
        </div>
      )}

      {/* FORM — two-column layout */}
      {section === "form" && (
        <div ref={formRef} style={{ padding: isMobile ? "16px" : "48px", maxWidth: 1200, width: "100%", boxSizing: "border-box" }}>
          {/* header */}
          <div style={{ marginBottom: 40 }}>
            <h2 style={{ fontSize: isMobile ? 24 : 32, fontWeight: 700, margin: "0 0 6px", textTransform: "none", letterSpacing: -0.5 }}>Solicitar presupuesto</h2>
            <p style={{ fontSize: 13, color: "#8a8a8a", margin: 0, textTransform: "none" }}>Completa los pasos y obtén un presupuesto estimado al instante.</p>
          </div>

          <StepIndicator current={step} />

          {/* two-column: form left + panel right */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 320px", gap: isMobile ? 24 : 48, alignItems: "start" }}>

            {/* LEFT — form steps */}
            <div>
              {/* STEP 0 — Tipo */}
              {step === 0 && (
                <div>
                  <GridSelect label="Tipo de sesión" value={form.sessionType} onChange={v => set("sessionType", v)} columns={2} options={[
                    { value: "photo_video", label: "Foto / Vídeo" },
                    { value: "event", label: "Eventos" },
                    { value: "showroom", label: "Pop-up / Showroom" },
                    { value: "workshop", label: "Formación" },
                  ]} />
                  <GridSelect label="Modalidad" value={form.modality} onChange={v => set("modality", v)} columns={2} options={[
                    { value: "jornada", label: "Jornada completa", detail: "8 h + 1 h cortesía" },
                    { value: "media", label: "Media jornada", detail: "4 h continuas" },
                  ]} />
                  <NumberInput label="Jornadas de montaje / desmontaje" value={form.mountDays} onChange={v => set("mountDays", v)} min={0} max={10}
                    sub="Días sin rodaje — tarifa reducida" />

                  <div style={{ fontSize: 12, color: "#8a8a8a", marginBottom: 24, textTransform: "none", letterSpacing: "0.02em", lineHeight: 1.6 }}>
                    Incluidos en todas las reservas: <strong style={{ color: "#8a8a8a" }}>limpieza</strong> y <strong style={{ color: "#8a8a8a" }}>pintado de ciclorama</strong>.<br />
                    Para servicios adicionales (catering, mobiliario, alquiler de material foto/vídeo) consulta la sección <span onClick={() => setSection("services")} style={{ color: "#000", textDecoration: "underline", cursor: "pointer" }}>Servicios</span>.
                  </div>
                </div>
              )}

              {/* STEP 1 — Producción */}
              {step === 1 && (
                <div>
                  <p style={{ fontSize: 14, color: "#8a8a8a", margin: "0 0 24px", textTransform: "none", letterSpacing: "0.02em", lineHeight: 1.6 }}>
                    Cuéntanos la magnitud de tu producción para preparar el espacio. Esto nos ayuda a tener todo listo el día del rodaje.
                  </p>

                  <GridSelect label="¿Cuántas personas seréis?" value={form.peopleBracket} onChange={v => set("peopleBracket", v)} columns={3} options={[
                    { value: "small", label: "1–10 personas", detail: "Equipo reducido — incluido" },
                    { value: "medium", label: "11–20 personas", detail: "Equipo medio" },
                    { value: "large", label: "21–30 personas", detail: "Producción grande" },
                    { value: "xlarge", label: "31–40 personas", detail: "Producción amplia" },
                    { value: "xxlarge", label: "41–50 personas", detail: "Aforo máximo" },
                    { value: "event", label: "Más de 50", detail: "Evento — a consultar" },
                  ]} />

                  {form.peopleBracket === "event" && (
                    <div style={{ fontSize: 13, color: "#c0392b", background: "#fff0f0", padding: "12px 16px", borderRadius: 0, marginBottom: 20, textTransform: "none", letterSpacing: "0.02em" }}>
                      Por encima del aforo máximo (50 personas) preparamos una propuesta a medida con los permisos de evento necesarios. Te contactaremos al recibir tu solicitud.
                    </div>
                  )}

                  <NumberInput label="Vehículos de descarga de material" value={form.vans} onChange={v => set("vans", v)} min={0} max={10}
                    sub="Hasta 2 vehículos incluidos. A partir del tercero se aplica suplemento." />

                  {form.vans > 2 && (
                    <div style={{ fontSize: 12, color: "#c57300", background: "#fff8ed", padding: "14px 16px", borderRadius: 0, marginBottom: 20, textTransform: "none", letterSpacing: "0.02em", lineHeight: 1.6 }}>
                      {form.vans - 2} vehículo{form.vans - 2 > 1 ? "s" : ""} extra — suplemento incluido en el presupuesto. Si necesitas estacionar camiones grandes en calle es necesario reservar plazas a través de la Comunidad de Madrid.
                    </div>
                  )}
                </div>
              )}

              {/* STEP 2 — Fecha */}
              {step === 2 && (
                <div>
                  <DateRangePicker label="Fecha(s) deseada(s)" value={form.date} onChange={v => set("date", v)} sub="Selecciona una fecha, o un rango si necesitas varios días" />
                  {form.date && baseDays > 1 && (
                    <div style={{ fontSize: 12, color: "#000", borderTop: "1px solid #e5e5e5", padding: "12px 0 0", marginBottom: 12, textTransform: "none", letterSpacing: "0.02em" }}>
                      Reserva de <strong>{baseDays} días</strong>
                      {hasSDF && lvDays > 0 && <> — {lvDays} laborable{lvDays > 1 ? "s" : ""} + {sdfDays} fin de semana / festivo</>}
                    </div>
                  )}
                  {hasSDF && (
                    <div style={{ fontSize: 12, color: "#c57300", background: "#fff8ed", padding: "14px 16px", borderRadius: 0, marginBottom: 20, textTransform: "none", letterSpacing: "0.02em", lineHeight: 1.6 }}>
                      {sdfDays === 1 ? "Tu reserva incluye 1 día" : `Tu reserva incluye ${sdfDays} días`} en fin de semana o festivo {sdfDateList.length <= 4 && (<>({sdfDateList.map(d => new Date(d + "T00:00:00").toLocaleDateString("es-ES", { day: "numeric", month: "short" })).join(", ")})</>)} — se aplica tarifa especial.
                    </div>
                  )}
                  <TextInput label="Descripción del proyecto" value={form.projectDesc} onChange={v => set("projectDesc", v)} placeholder="Ej: Sesión editorial de moda, lookbook 30 looks..." />
                </div>
              )}

              {/* STEP 3 — Datos */}
              {step === 3 && (
                <form onSubmit={e => e.preventDefault()} autoComplete="on">
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <TextInput
                      label="Nombre / Razón social *"
                      value={form.name}
                      onChange={v => set("name", v)}
                      placeholder="Tu nombre o empresa"
                      name="name"
                      autoComplete="name"
                      autoCapitalize="words"
                    />
                    <TextInput
                      label="Empresa / Productora"
                      value={form.company}
                      onChange={v => set("company", v)}
                      placeholder="Nombre de la productora"
                      name="organization"
                      autoComplete="organization"
                      autoCapitalize="words"
                    />
                    <TextInput
                      label="Email *"
                      value={form.email}
                      onChange={v => set("email", v)}
                      placeholder="tu@email.com"
                      type="email"
                      name="email"
                      autoComplete="email"
                      inputMode="email"
                      autoCapitalize="off"
                      spellCheck={false}
                      error={emailError}
                    />
                    <TextInput
                      label="Teléfono *"
                      value={form.phone}
                      onChange={v => set("phone", v)}
                      placeholder="+34 600 000 000"
                      type="tel"
                      name="tel"
                      autoComplete="tel"
                      inputMode="tel"
                      autoCapitalize="off"
                      spellCheck={false}
                      error={phoneError}
                    />
                    <TextInput
                      label="NIF / CIF"
                      value={form.nif}
                      onChange={v => set("nif", v)}
                      placeholder="Opcional"
                      name="tax-id"
                      autoComplete="off"
                      autoCapitalize="characters"
                      spellCheck={false}
                    />
                  </div>

                  <label style={{ marginTop: 14, display: "flex", alignItems: "flex-start", gap: 10, fontSize: 11.5, color: "#777", lineHeight: 1.5, cursor: "pointer", textTransform: "none", letterSpacing: "0.02em" }}>
                    <input
                      type="checkbox"
                      checked={form.irpfRetention}
                      onChange={e => set("irpfRetention", e.target.checked)}
                      style={{ marginTop: 2, width: 14, height: 14, accentColor: "#000", flexShrink: 0, cursor: "pointer" }}
                    />
                    <span>
                      Soy autónomo o empresa en España
                    </span>
                  </label>

                  <div style={{ marginTop: 28, display: "flex", flexDirection: "column", gap: 16 }}>
                    <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 11.5, color: "#777", lineHeight: 1.5, cursor: "pointer", textTransform: "none", letterSpacing: "0.02em" }}>
                      <input
                        type="checkbox"
                        checked={form.privacyConsent}
                        onChange={e => set("privacyConsent", e.target.checked)}
                        style={{ marginTop: 2, width: 14, height: 14, accentColor: "#000", flexShrink: 0, cursor: "pointer" }}
                      />
                      <span>
                        He leído y acepto la <span onClick={e => { e.preventDefault(); setSection("privacy"); }} style={{ color: "#000", textDecoration: "underline", cursor: "pointer" }}>política de privacidad</span> para que gestionéis y me enviéis este presupuesto. <span style={{ color: "#8a8a8a", fontSize: 10.5, marginLeft: 2 }}>(obligatorio)</span>
                      </span>
                    </label>

                    <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 11.5, color: "#777", lineHeight: 1.5, cursor: "pointer", textTransform: "none", letterSpacing: "0.02em" }}>
                      <input
                        type="checkbox"
                        checked={form.marketingConsent}
                        onChange={e => set("marketingConsent", e.target.checked)}
                        style={{ marginTop: 2, width: 14, height: 14, accentColor: "#000", flexShrink: 0, cursor: "pointer" }}
                      />
                      <span>
                        Quiero ser miembro del club Casa Yubero: fechas exclusivas, eventos privados en el estudio y tarifas especiales para suscriptores.
                      </span>
                    </label>
                  </div>
                </form>
              )}

              {/* STEP 4 — Presupuesto final */}
              {step === 4 && (
                <div>
                  {leadSent && (
                    <div style={{ textAlign: "center", padding: isMobile ? "32px 0 24px" : "40px 0 28px" }}>
                      <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#f0faf4", border: "2px solid #b7e4c7", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#1a7a40" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </div>
                      <h2 style={{ fontSize: isMobile ? 22 : 26, fontWeight: 700, margin: "0 0 10px", textTransform: "none", letterSpacing: -0.3 }}>¡Listo! Presupuesto enviado</h2>
                      <p style={{ fontSize: 14, color: "#8a8a8a", margin: "0 0 4px", textTransform: "none", letterSpacing: "0.01em" }}>
                        Te llegará en breve a <strong style={{ color: "#000" }}>{form.email}</strong>
                      </p>
                      <p style={{ fontSize: 13, color: "#8a8a8a", margin: 0, textTransform: "none" }}>Revisa también la carpeta de spam · Te escribimos en menos de 24h</p>
                    </div>
                  )}

                  <div style={{ borderTop: "1px solid #e5e5e5", padding: "20px 0 0", marginBottom: 24, fontSize: 13, color: "#8a8a8a", lineHeight: 1.8, textTransform: "none", letterSpacing: "0.02em" }}>
                    <p style={{ margin: "0 0 2px" }}><strong style={{ color: "#000" }}>Cliente:</strong> {form.name} {form.company && `(${form.company})`}</p>
                    <p style={{ margin: "0 0 2px" }}><strong style={{ color: "#000" }}>Contacto:</strong> {form.email} · {form.phone}</p>
                    <p style={{ margin: "0 0 2px" }}><strong style={{ color: "#000" }}>Fecha(s):</strong> {(() => { const [s, e] = form.date.split("|"); return fmtRange(s, e || ""); })()}</p>
                    <p style={{ margin: "0 0 2px" }}><strong style={{ color: "#000" }}>Sesión:</strong> {sessionLabels[form.sessionType] || form.sessionType} · {modalityLabel[form.modality]}{hasSDF ? ` · incluye ${sdfDays} día${sdfDays > 1 ? "s" : ""} en fin de semana / festivo` : ""}</p>
                    <p style={{ margin: "0 0 2px" }}><strong style={{ color: "#000" }}>Producción:</strong> {bracketSup.label}{form.vans > 0 ? ` · ${form.vans} vehículo${form.vans > 1 ? "s" : ""} de descarga` : ""}</p>
                    {form.projectDesc && <p style={{ margin: "2px 0 0" }}><strong style={{ color: "#000" }}>Proyecto:</strong> {form.projectDesc}</p>}
                  </div>

                  <table style={{ width: "100%", fontSize: 14, borderCollapse: "collapse", marginBottom: 24, textTransform: "none", letterSpacing: "0.02em" }}>
                    <thead>
                      <tr style={{ borderBottom: "2px solid #000" }}>
                        <th style={{ textAlign: "left", padding: "10px 0", fontWeight: 700, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" }}>Conceptos incluidos</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ borderBottom: "1px solid #e5e5e5" }}>
                        <td style={{ padding: "12px 0" }}>
                          Alquiler espacio — {modalityLabel[form.modality]}{baseDays > 1 ? ` × ${baseDays} días` : ""}
                          {(bracketSup.amount != null && bracketSup.amount > 0) && ` · ${bracketSup.label}`}
                          {vanSup > 0 && ` · ${form.vans} vehículo${form.vans > 1 ? "s" : ""} de descarga`}
                        </td>
                      </tr>
                      {cleaning > 0 && (
                        <tr style={{ borderBottom: "1px solid #e5e5e5" }}>
                          <td style={{ padding: "12px 0" }}>Limpieza</td>
                        </tr>
                      )}
                      {cyclorama > 0 && (
                        <tr style={{ borderBottom: "1px solid #e5e5e5" }}>
                          <td style={{ padding: "12px 0" }}>Pintado de ciclorama</td>
                        </tr>
                      )}
                      {mount > 0 && (
                        <tr style={{ borderBottom: "1px solid #e5e5e5" }}>
                          <td style={{ padding: "12px 0" }}>Montaje / desmontaje — {form.mountDays} jornada(s) al 50%</td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {hasConvenir && (
                    <div style={{ background: "#fff8ed", border: "1px solid #ffd591", borderRadius: 0, padding: "14px 18px", fontSize: 13, color: "#c57300", marginBottom: 16, textTransform: "none", letterSpacing: "0.02em" }}>
                      Presupuesto personalizado — te contactaremos para cerrar cifras.
                    </div>
                  )}

                  <div style={{ borderTop: "1px solid #e5e5e5", padding: "16px 0 0", fontSize: 13, color: "#8a8a8a", marginBottom: 16, textTransform: "none", letterSpacing: "0.02em", lineHeight: 1.7 }}>
                    Recibirás el <strong style={{ color: "#000" }}>presupuesto detallado con importes, IVA y fianza</strong> junto con las condiciones de alquiler en un único PDF en tu correo.
                  </div>

                  <div style={{ marginTop: 28, display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <button onClick={() => { setStep(0); setLeadSent(false); setSendError(""); setForm(INITIAL_FORM); }}
                      style={{ padding: "14px 24px", background: "#fff", color: "#000", border: "1px solid #e5e5e5", borderRadius: 0, fontSize: 13, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.08em" }}>
                      Nuevo presupuesto
                    </button>
                  </div>
                </div>
              )}

              {/* NAV BUTTONS */}
              {step < 4 && (
                <>
                  {step === 3 && sendError && (
                    <div style={{ background: "#fdecea", border: "1px solid #f5b7b1", borderRadius: 0, padding: "12px 16px", fontSize: 13, color: "#c0392b", marginTop: 24, textTransform: "none", letterSpacing: "0.02em" }}>
                      {sendError}
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 44 }}>
                    <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0 || sending}
                      style={{ padding: "14px 28px", background: "#fff", border: "1px solid #e5e5e5", borderRadius: 0, fontSize: 13, cursor: step === 0 || sending ? "default" : "pointer", opacity: step === 0 || sending ? .3 : 1, fontFamily: "inherit", letterSpacing: "0.08em" }}>
                      ← Atrás
                    </button>
                    <button
                      onClick={async () => {
                        if (step === 3) {
                          const ok = await sendBudget();
                          if (ok) setStep(4);
                        } else {
                          setStep(s => s + 1);
                        }
                      }}
                      disabled={!canNext() || sending}
                      style={{ padding: "14px 36px", background: (canNext() && !sending) ? "#000" : "#e5e5e5", color: (canNext() && !sending) ? "#fff" : "#8a8a8a", border: "none", borderRadius: 0, fontSize: 13, fontWeight: 700, cursor: (canNext() && !sending) ? "pointer" : "default", fontFamily: "inherit", letterSpacing: "0.08em" }}>
                      {step === 3 ? (sending ? "Enviando…" : "Enviarme el presupuesto") : "Siguiente →"}
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* RIGHT — sticky price panel */}
            <PricePanel
              base={base} baseDays={baseDays} mount={mount} bracketSup={bracketSup} vanSup={vanSup}
              cleaning={cleaning} cyclorama={cyclorama}
              iva={iva} total={total} subtotal={subtotal}
              deposit={deposit} hasConvenir={hasConvenir} step={step}
            />
          </div>
        </div>
      )}

      {/* CÓMO LLEGAR */}
      {section === "location" && (
        <div style={{ padding: `48px ${px}`, maxWidth: 860 }}>
          <button onClick={() => setSection("home")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#8a8a8a", fontFamily: "inherit", letterSpacing: "0.05em", padding: 0, marginBottom: 28 }}>← Volver</button>
          <h2 style={{ fontSize: isMobile ? 26 : 36, fontWeight: 700, margin: "0 0 6px", textTransform: "none", letterSpacing: -0.5 }}>Cómo llegar</h2>
          <p style={{ fontSize: 14, color: "#8a8a8a", margin: "0 0 32px", textTransform: "none", letterSpacing: "0.01em" }}>
            Calle Abolengo 12, Carabanchel, Madrid 28025
          </p>
          <div style={{ borderRadius: 0, overflow: "hidden", marginBottom: 36, border: "1px solid #e5e5e5" }}>
            <iframe title="Mapa Casa Yubero" src="https://maps.google.com/maps?q=Calle+Abolengo+12,+28025+Madrid&output=embed&z=16"
              width="100%" height={isMobile ? 260 : 380} style={{ display: "block", border: 0 }}
              loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 16, marginBottom: 32 }}>
            {[
              { title: "Metro", lines: ["Vista Alegre · Línea 5 · 1 min a pie", "Oporto · Línea 5 · 7 min a pie"] },
              { title: "Autobús EMT", lines: ["Línea 17, 18, 34 · parada cercana", "Línea 116, 118"] },
              { title: "En coche", lines: ["Parking en calle (zona verde)", "Acceso directo por fachada para carga/descarga"] },
            ].map(({ title, lines }) => (
              <div key={title} style={{ padding: "20px 22px", border: "1px solid #e5e5e5", borderRadius: 0, background: "#fff" }}>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", color: "#8a8a8a", marginBottom: 10 }}>{title.toUpperCase()}</div>
                {lines.map(l => <p key={l} style={{ fontSize: 13, color: "#8a8a8a", margin: "0 0 4px", textTransform: "none", letterSpacing: "0.01em", lineHeight: 1.5 }}>{l}</p>)}
              </div>
            ))}
          </div>
          <a href="https://maps.google.com/?q=Calle+Abolengo+12,+28025+Madrid" target="_blank" rel="noopener noreferrer"
            style={{ display: "inline-block", padding: "14px 28px", background: "#000", color: "#fff", borderRadius: 0, fontSize: 13, fontWeight: 700, textDecoration: "none", letterSpacing: "0.08em" }}>
            ABRIR EN GOOGLE MAPS
          </a>
        </div>
      )}

      </div>{/* end .section-fade */}

      {/* GDPR NOTICE */}
      {!privacyDismissed && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 50, background: "#000", color: "#fff", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap", fontSize: 12, letterSpacing: "0.03em" }}>
          <span style={{ textTransform: "none", lineHeight: 1.5 }}>
            Este sitio no usa cookies de seguimiento. Al usarlo aceptas nuestra{" "}
            <button onClick={() => setSection("privacy")} style={{ background: "none", border: "none", color: "#fff", textDecoration: "underline", cursor: "pointer", fontFamily: "inherit", fontSize: "inherit", padding: 0, letterSpacing: "inherit" }}>política de privacidad</button>.
          </span>
          <button
            onClick={() => { try { localStorage.setItem("cy_privacy", "1"); } catch {} setPrivacyDismissed(true); }}
            style={{ background: "#fff", color: "#000", border: "none", borderRadius: 0, padding: "8px 18px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>
            ENTENDIDO
          </button>
        </div>
      )}

      {/* FOOTER */}
      <footer style={{ padding: `40px ${px}`, borderTop: "1px solid #e5e5e5", marginTop: 40, fontSize: 12, color: "#8a8a8a" }}>
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 16, flexDirection: isMobile ? "column" : "row" }}>
          <div style={{ textTransform: "none", letterSpacing: "0.02em" }}>
            <strong style={{ color: "#000", fontSize: 13, fontFamily: "inherit" }}>Casa Yubero</strong><br />
            Calle Abolengo 12, Carabanchel, Madrid — 190 m² · Estudio de fotografía y vídeo
          </div>
          <div style={{ textAlign: isMobile ? "left" : "right", textTransform: "none", letterSpacing: "0.02em" }}>
            hello@casayubero.com · +34 657 864 373<br />
            <a href="https://www.instagram.com/casayubero" target="_blank" rel="noopener noreferrer" style={{ color: "#8a8a8a", textDecoration: "none" }}>@casayubero</a>
            {" · "}
            <span onClick={() => setSection("privacy")} style={{ cursor: "pointer", textDecoration: "underline", color: "#8a8a8a" }}>Política de privacidad</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
